import _ from 'lodash';
import { connect } from 'react-redux';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import DatePicker from 'react-datepicker';
import { NumericFormat } from 'react-number-format';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CircleCheckBig, Calendar, TriangleAlert, CircleHelp, Sparkles, ChevronDown, ChevronUp, Undo2, FileSearch, Award } from 'lucide-react';
import React, { Fragment, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import CustomSelectWithTooltip from './CustomSelectWithTooltip';
import { Actions as ADMINACTIONS } from '../../store/actions/adminActions';
import { Actions as ORGANISATIONACTIONS } from '../../store/actions/organisationActions';
import { Actions as USAGEACTIONS } from '../../store/actions/usageActions';
import {
  getUsageOptionsByFilteredCF,
  getFactorYear,
  isBlankDimension,
  sortUsageDimensionKeys,
  usageDimensionOrder,
} from '../UsageData/utils/UsageUtils';
import { addYears, differenceInDays, format, formatISO, isLeapYear, parseISO } from 'date-fns';
import { parseDateToTz, isValidDateFormat, RemoveLeadingZeroes } from '../utils/GlobalFunctions';
import { BetaTag, WaveformLoader } from '../Common/ReusableComponents/Reusable';
import QualityTierBadge from '../Quality/QualityTierBadge';
import { getUsageQuality } from '../../utils/qualityHelpers';

/** Popper renders into this node on document.body so the calendar is not clipped by modal overflow. */
const DATEPICKER_PORTAL_ID = 'datepicker-portal-root';

export const itemToOptions = (item) => ({
  value: item?.id,
  label: item?.name || item?.priceType,
  description: item?.description,
  // for sub entities we need the metric and calc_market property
  metric: item?.metric || null,
  fuel_mix: item?.calc_market || null,
});

const normalizeClarifyItems = (items) => {
  if (!items) return [];
  const mapOptions = (rawOpts) => {
    if (!Array.isArray(rawOpts) || rawOpts.length === 0) return null;
    return rawOpts.map((o) =>
      typeof o === 'string'
        ? { label: o, value: o }
        : {
            label: String(o.label ?? o.name ?? o.description ?? o.value ?? o.id ?? o.code ?? ''),
            value: o.value ?? o.id ?? o.code ?? o.label,
          },
    );
  };
  if (Array.isArray(items)) {
    return items.map((raw, idx) => {
      const idRaw = raw.id ?? raw.key ?? raw.field ?? raw.name ?? `item_${idx}`;
      const key = String(idRaw);
      const label = String(raw.prompt ?? raw.label ?? raw.question ?? raw.message ?? raw.text ?? key);
      const kind = raw.kind != null ? String(raw.kind) : null;
      const options = mapOptions(raw.options ?? raw.choices);
      const allow_free_text = raw.allow_free_text === true;
      const preselected = raw.preselected != null ? String(raw.preselected) : null;
      return { id: key, key, kind, label, options, allow_free_text, preselected };
    });
  }
  if (typeof items === 'object') {
    return Object.entries(items).map(([key, v]) => ({
      id: key,
      key,
      kind: typeof v === 'object' && v?.kind != null ? String(v.kind) : null,
      label: typeof v === 'string' ? v : String(v?.prompt ?? v?.label ?? v?.question ?? key),
      options: mapOptions(v?.options ?? v?.choices),
      allow_free_text: typeof v === 'object' && v?.allow_free_text === true,
    }));
  }
  return [];
};

const catalogUuidFromUsageField = (field) => {
  if (field == null || field === '') return undefined;
  if (typeof field === 'object' && field.value != null) {
    const s = String(field.value).trim();
    return s || undefined;
  }
  const s = String(field).trim();
  return s || undefined;
};

/**
 * Maps accumulated clarify_replies to originlocation/finallocation using kind per question id
 * (kinds come from every clarify round — not only the latest clarify_items).
 */
const clarifyCatalogOverridesFromAllReplies = (replies, kindById) => {
  let originlocation;
  let finallocation;
  if (!replies || !kindById) return { originlocation, finallocation };
  Object.entries(replies).forEach(([id, v]) => {
    if (v == null || !String(v).trim()) return;
    const val = String(v).trim();
    const kind = kindById[id];
    if (kind === 'origin_location') originlocation = val;
    if (kind === 'final_location') finallocation = val;
  });
  return { originlocation, finallocation };
};

const isClarifyRowAnswered = (row, reply) => {
  const r = reply != null ? String(reply).trim() : '';
  if (!r) return false;
  const opts = row.options;
  if (!opts?.length) return true;
  if (row.allow_free_text) return true;
  return opts.some((o) => String(o.value) === r);
};

const getClarifyOptionMatch = (row, reply) => {
  const r = reply ?? '';
  if (!row?.options?.length || r === '') return undefined;
  return row.options.find((o) => String(o.value) === String(r));
};

/** Text shown in the optional clarify field: label when a chip UUID is selected, otherwise the user’s custom text. */
const clarifyFreeTextInputDisplay = (row, reply) => {
  const r = reply ?? '';
  const fromChip = getClarifyOptionMatch(row, reply);
  if (fromChip) return fromChip.label;
  return String(r);
};

/** Maps API clarify `kind` to usage option / metric.types keys (vehicle, fuel, …). */
const CLARIFY_KIND_TO_USAGE_KEY = {
  vehicle_type: 'vehicle',
  fuel_type: 'fuel',
  seat_type: 'seat',
  production_type: 'production',
  waste_type: 'waste',
  material_subtype: 'waste',
  origin_location: 'originlocation',
  final_location: 'finallocation',
  entity_type: null,
  metric_type: null,
  unit_missing: null,
  packaging_conversion: null,
};

const findCatalogRowName = (value, list) => {
  if (value == null || !list?.length) return null;
  const row = list.find((x) => String(x.id) === String(value));
  return row?.name || row?.priceType || null;
};

const resolveClarifyOptionLabel = (value, row, { usageOptions, metricTypes, subentities }) => {
  if (value == null || value === '') return null;
  const v = String(value);

  if (String(row.kind) === 'subentity_detail') {
    return findCatalogRowName(v, subentities);
  }

  if (['entity_type', 'metric_type', 'unit_missing', 'packaging_conversion'].includes(String(row.kind))) {
    return null;
  }

  const usageKey = CLARIFY_KIND_TO_USAGE_KEY[row.kind];
  if (usageKey) {
    const byUsage = findCatalogRowName(v, usageOptions?.[usageKey]);
    if (byUsage) return byUsage;
    const byTypes = findCatalogRowName(v, metricTypes?.[usageKey]);
    if (byTypes) return byTypes;
  }

  if (usageOptions && typeof usageOptions === 'object') {
    for (const list of Object.values(usageOptions)) {
      if (!Array.isArray(list)) continue;
      const name = findCatalogRowName(v, list);
      if (name) return name;
    }
  }

  if (metricTypes && typeof metricTypes === 'object') {
    for (const list of Object.values(metricTypes)) {
      if (!Array.isArray(list)) continue;
      const name = findCatalogRowName(v, list);
      if (name) return name;
    }
  }

  return null;
};

/**
 * When the API sends placeholder labels (label === value), resolve human names from Redux usage/metric catalogs.
 */
const enrichClarifyRowOptions = (row, ctx) => {
  if (!row?.options?.length) return row;
  return {
    ...row,
    options: row.options.map((opt) => {
      const rawLabel = opt.label != null ? String(opt.label) : '';
      const rawVal = opt.value != null ? String(opt.value) : '';
      const needsResolve = !rawLabel || rawLabel === rawVal;
      const resolved = needsResolve ? resolveClarifyOptionLabel(opt.value, row, ctx) : null;
      return { ...opt, label: resolved || rawLabel || rawVal };
    }),
  };
};

/** Parse numeric usage from item (may be number or display string e.g. "1234.00 kg"). */
function parseUsageNumber(val) {
  if (val == null || val === '') return null;
  const n = Number(val);
  if (!Number.isNaN(n)) return n;
  if (typeof val === 'string') {
    const match = val.match(/^[\d.,\s]+/);
    if (match) return Number(String(match[0]).replace(/,/g, '').trim()) || null;
  }
  return null;
}

/**
 * Top-level `unit_conversion` on factor-suggest (xeroapi `buildFactorSuggestUnitConversionPayload`).
 * Same conversion is mirrored in `usage_derivation.conversions` as `rule_id: unit_normalization` for audit;
 * prefer showing that row in “How this quantity was calculated” — this string is for the pre-apply hint
 * and legacy payloads. Prefer `message`, then `description`. Client-side: `usage.amount * factor` ≈
 * `converted_amount` in `to_unit` when recomputing.
 * @param {object|null|undefined} res
 * @returns {string|null}
 */
function getFactorSuggestUnitConversionDisplayText(res) {
  if (!res || typeof res !== 'object') return null;

  const uc = res.unit_conversion;
  if (uc && typeof uc === 'object') {
    const msg = uc.message != null ? String(uc.message).trim() : '';
    const desc = uc.description != null ? String(uc.description).trim() : '';
    if (msg || desc) {
      return msg || desc;
    }

    const from = uc.from_unit != null && uc.from_unit !== '' ? String(uc.from_unit) : '';
    const to = uc.to_unit != null && uc.to_unit !== '' ? String(uc.to_unit) : '';
    const factorNum =
      uc.factor !== undefined && uc.factor !== null && uc.factor !== '' ? Number(uc.factor) : NaN;
    const convRaw = uc.converted_amount;
    const convNum = convRaw !== undefined && convRaw !== null && convRaw !== '' ? Number(convRaw) : NaN;

    if (from && to) {
      const parts = [];
      parts.push(`Conversion for this factor: ${from} → ${to}.`);
      if (!Number.isNaN(factorNum)) {
        parts.push(`1 ${from} = ${factorNum} ${to}.`);
      }
      if (!Number.isNaN(convNum)) {
        parts.push(`Pre-filled usage: ${convNum}${to ? ` ${to}` : ''}.`);
      }
      return parts.join(' ');
    }

    if (!Number.isNaN(convNum) && to) {
      return `Pre-filled usage: ${convNum} ${to}.`;
    }
  }

  const u = res.usage;
  const uifu = res.usage_in_factor_units;
  const uUnit = u?.unit != null && String(u.unit).trim() !== '' ? String(u.unit).trim() : '';
  const iUnit = uifu?.unit != null && String(uifu.unit).trim() !== '' ? String(uifu.unit).trim() : '';
  if (
    u?.amount != null &&
    u.amount !== '' &&
    uifu?.amount != null &&
    uifu.amount !== '' &&
    uUnit &&
    iUnit &&
    uUnit.toLowerCase() !== iUnit.toLowerCase()
  ) {
    return `Adjusted from ${u.amount} ${uUnit} to ${uifu.amount} ${iUnit} for the usage field.`;
  }

  return null;
}

/**
 * Pre-apply quantity hint: `unit_conversion` / `usage_in_factor_units`. After apply, use
 * `usage_derivation.conversions` (including `unit_normalization`) in the disclosure next to usage.
 * @param {object|null|undefined} res
 * @returns {string|null}
 */
function getFactorSuggestUsageQuantityExplanation(res) {
  const fromConversion = getFactorSuggestUnitConversionDisplayText(res);
  if (fromConversion) return fromConversion;

  const uifu = res?.usage_in_factor_units;
  if (uifu?.amount != null && uifu.amount !== '') {
    return `Suggested usage for this factor: ${uifu.amount}${uifu.unit ? ` ${uifu.unit}` : ''} (metric field units).`;
  }

  return null;
}

/** True when `usage_derivation.conversions` has audit rows (paper, flight, packaging LLM, `unit_normalization`, …). */
function hasUsageDerivationQuantityConversions(res) {
  const list = res?.usage_derivation?.conversions;
  return Array.isArray(list) && list.length > 0;
}

/** Backend mirrors unit rescaling here (`rule_id: unit_normalization`); avoid duplicating the fallback card. */
function derivationsIncludeUnitNormalization(res) {
  const list = res?.usage_derivation?.conversions;
  if (!Array.isArray(list)) return false;
  return list.some((c) => String(c.rule_id) === 'unit_normalization');
}

/** Legacy / edge: top-level `unit_conversion` only, no mirrored `conversions[]` row yet. */
function hasUnitConversionWithoutDerivationMirror(res) {
  if (derivationsIncludeUnitNormalization(res)) return false;
  const uc = res?.unit_conversion;
  if (!uc || typeof uc !== 'object') return false;
  if (String(uc.message || '').trim()) return true;
  if (String(uc.description || '').trim()) return true;
  if (uc.from_unit && uc.to_unit) return true;
  return false;
}

const AddEditUsage = ({
  item,
  metric,
  prevObj,
  options,
  currency,
  branding,
  prevDates,
  showLoading,
  gridSources,
  adminPortal,
  cyfDataAdmin,
  getUsageById,
  usageDetails,
  orderPriority,
  saveUsageData,
  updateUsageYears,
  resetUsageDataModal,
  progressState,
  solarConsumed,
  editUsageModal,
  waterTreatment,
  usageOptionsOK,
  usageOptions,
  metricTypes,
  getUsageOptions,
  usageTypeDetails,
  solarSubentities,
  getAllItemsFromDB,
  conversionFactors,
  getFilteredFactors,
  showUsageDataModal,
  electricitySources,
  organisationCurrency,
  suppliersEmissionsFactor,
  useSupplierCarbonFactor,
  metrics,
  seeDetails,
  font,
  factors,
  factorDetails,
  feature,
  aprilFools,
  hasAprilFools,
  user,
  orgCountryAlpha2,
  subentities,
  suggestFactorFromText,
  applyFactorSuggestionToModal,
  submitFactorSuggestFeedback,
  clearFactorSuggestCache,
}) => {
  // Local States
  const [showMore, setShowMore] = useState(false);
  const [showFactorDetails, setShowFactorDetails] = useState(false);
  const [showFuelImport, setShowFuelImport] = useState(false);
  const [isSavingUsage, setIsSavingUsage] = useState(false);
  const [invalidUsageDate, setInvalidUsageDate] = useState(false);
  const [hasFuelMix, setHasFuelMix] = useState(item?.fuel_mix ? true : false);
  const [localGridMix, setLocalGridMix] = useState(item?.fuel_mix || { 599324: '100' });

  // Supplier factor is now handled in its own state.
  const [hasSupplierFactor, setHasSupplierFactor] = useState(item?.useSupplierCarbonFactor || 'No');
  const [supplierFactor, setSupplierFactor] = useState(item?.suppliersEmissionsFactor ?? null);

  const [showPreviousDiv, setShowPreviousDiv] = useState(false);
  const [localTextState, setLocalTextState] = useState({
    userLabel: null,
    usage: null,
    waterTreatment: null,
    solarConsumed: null,
    suppliersEmissionsFactor: null,
  });
  // Read during render (usage validation and the input's value), so it has to be state:
  // mutating a ref does not schedule a re-render, which can leave the submit button one render behind.
  const [hasUserInteractedWithUsage, setHasUserInteractedWithUsage] = useState(false);
  /** Maps clarify_items[].id -> kind across all rounds (for originlocation/finallocation body fields). */
  const clarifyItemKindByIdRef = useRef({});
  const [isLocationVisible, setIsLocationVisible] = useState(true);
  const [isMarketVisible, setIsMarketVisible] = useState(true);

  const [activitySuggestText, setActivitySuggestText] = useState('');
  const [factorSuggestRequest, setFactorSuggestRequest] = useState(null);
  const [factorSuggestResponse, setFactorSuggestResponse] = useState(null);
  const [clarifyReplies, setClarifyReplies] = useState({});
  const [factorSuggestApplied, setFactorSuggestApplied] = useState(null);
  const [alternatesOpen, setAlternatesOpen] = useState(false);
  const [rememberChoice, setRememberChoice] = useState(true);
  const [derivationOpen, setDerivationOpen] = useState(false);
  const [conversionOpen, setConversionOpen] = useState(false);

  const savedUsageSnapshotRef = useRef(null);
  const saveProgressSeenRef = useRef(false);

  const resetFactorSuggestLocalState = useCallback(() => {
    setActivitySuggestText('');
    setFactorSuggestResponse(null);
    setClarifyReplies({});
    clarifyItemKindByIdRef.current = {};
    setFactorSuggestApplied(null);
    setAlternatesOpen(false);
    setRememberChoice(true);
    setDerivationOpen(false);
    setConversionOpen(false);
    setFactorSuggestRequest(null);
  }, []);

  const toggleLocationVisibility = () => setIsLocationVisible(!isLocationVisible);
  const toggleMarketVisibility = () => setIsMarketVisible(!isMarketVisible);

  const { primaryColor, tooltipBackground, tooltipText } = branding || {};
  const factorSuggestUiEnabled = process.env.REACT_APP_ENABLE_FACTOR_SUGGEST !== 'false';

  const localGridChange = (value, name) => {
    if (!hasFuelMix) {
      setLocalGridMix({ 599324: '100' });
    } else {
      const prevMix = { ...localGridMix };
      prevMix[name] = parseFloat(value) <= 0 ? '0' : RemoveLeadingZeroes(value);
      // Delete the default residual key if multiple values exist
      if (Object.keys(prevMix).length > 1) delete prevMix['599324'];
      setLocalGridMix(prevMix);
    }
  };

  const fuelMixSum = Number(
    Object.values(localGridMix)
      .reduce((a, b) => Number(a) + Number(b), 0)
      .toFixed(11)
  );

  const showButton =
    item?.dateEnd &&
    item?.facility &&
    usageOptionsOK &&
    item?.subentity &&
    item?.dateStart &&
    item?.metric &&
    !invalidUsageDate &&
    !!conversionFactors?.length > 0 &&
    (showFuelImport ? fuelMixSum === 100 : true) &&
    new Date(item?.dateStart) <= new Date(item?.dateEnd);

  // Use item's usage for submit check until user has interacted with the field (avoids "Data Incomplete" before effect syncs local state).
  const effectiveUsage = hasUserInteractedWithUsage
    ? localTextState?.usage
    : (localTextState?.usage ?? item?.rawUsage ?? item?.usage);

  // Parse numeric value (item.usage may be display string e.g. "1234.00 kg" from API).
  const usageOk = (parseUsageNumber(effectiveUsage) ?? 0) > 0;

  const canSubmitUsage = useMemo(
    () =>
      Boolean(
        item?.dateEnd &&
          item?.facility &&
          usageOptionsOK &&
          item?.subentity &&
          item?.dateStart &&
          item?.metric &&
          !invalidUsageDate &&
          (!!item?.id || !!conversionFactors?.length) &&
          usageOk &&
          (showFuelImport
            ? fuelMixSum === 100 || (fuelMixSum === 0 && localTextState?.suppliersEmissionsFactor != null)
            : true) &&
          new Date(item?.dateStart) <= new Date(item?.dateEnd),
      ),
    [
      item?.dateEnd,
      item?.dateStart,
      item?.facility,
      item?.subentity,
      item?.metric,
      item?.id,
      usageOptionsOK,
      invalidUsageDate,
      conversionFactors?.length,
      usageOk,
      showFuelImport,
      fuelMixSum,
      localTextState?.suppliersEmissionsFactor,
    ],
  );

  const formatDateEndForApi = (dateEnd) => {
    if (!dateEnd) return null;
    try {
      return format(parseISO(dateEnd), 'yyyy-MM-dd');
    } catch {
      try {
        return format(new Date(dateEnd), 'yyyy-MM-dd');
      } catch {
        return null;
      }
    }
  };

  const factorSuggestPrereqsOk =
    Boolean(item?.facility && item?.dateStart && item?.dateEnd && !invalidUsageDate);

  const describeFactorCandidate = (c) => {
    if (!c) return '';
    if (c.factor_display_name) return c.factor_display_name;
    const factorName = c.factor_name || '';
    const subName = _.find(subentities, (s) => String(s.id) === String(c.subentity))?.name;
    const metricName = _.find(metrics, (m) => String(m.id) === String(c.metric))?.name;
    const parts = [factorName];
    if (c.subentity_description && !factorName.includes(c.subentity_description)) {
      parts.push(c.subentity_description);
    }
    if (subName && !factorName.includes(subName)) {
      parts.push(`Usage sub-type: ${subName}`);
    }
    if (metricName && !factorName.includes(metricName)) {
      parts.push(`Metric: ${metricName}`);
    }
    return parts.filter(Boolean).join(' · ') || 'Suggested match';
  };

  const factorSuggestClarifyRowsResolved = useMemo(() => {
    if (!factorSuggestResponse?.clarify_items) return [];
    const raw = normalizeClarifyItems(factorSuggestResponse.clarify_items);
    const ctx = { usageOptions: usageOptions || {}, metricTypes: metricTypes || {}, subentities };
    return raw.map((row) => enrichClarifyRowOptions(row, ctx));
  }, [factorSuggestResponse, usageOptions, metricTypes, subentities]);

  useEffect(() => {
    const status = factorSuggestResponse?.suggest_status;
    if (status !== 'clarify' || !factorSuggestResponse?.clarify_items) return;
    const rows = normalizeClarifyItems(factorSuggestResponse.clarify_items);
    rows.forEach((r) => {
      if (r.kind) clarifyItemKindByIdRef.current[r.key] = r.kind;
    });
    setClarifyReplies((prev) => {
      const echoed = factorSuggestResponse.clarify_context?.previous_replies;
      const next = { ...prev };
      if (echoed && typeof echoed === 'object') {
        Object.entries(echoed).forEach(([k, v]) => {
          const hasLocal = next[k] != null && String(next[k]).trim() !== '';
          if (!hasLocal && v != null) {
            next[k] = typeof v === 'object' ? JSON.stringify(v) : String(v);
          }
        });
      }
      rows.forEach((r) => {
        if (!(r.key in next)) next[r.key] = r.preselected || '';
      });
      return next;
    });
  }, [factorSuggestResponse]);

  const handleFactorSuggest = async (includeClarifyReplies = false) => {
    if (!factorSuggestPrereqsOk) return;
    const date_end = formatDateEndForApi(item.dateEnd);
    if (!date_end) return;
    const date_start = item.dateStart ? formatDateEndForApi(item.dateStart) : undefined;
    if (!includeClarifyReplies) {
      setClarifyReplies({});
      clarifyItemKindByIdRef.current = {};
      setFactorSuggestApplied(null);
      setRememberChoice(true);
      setDerivationOpen(false);
      setConversionOpen(false);
      setFactorSuggestResponse(null);
    }
    setAlternatesOpen(false);
    setFactorSuggestRequest(includeClarifyReplies ? 'clarify' : 'suggest');
    const payload = {
      text: activitySuggestText,
      date_end,
      org_country: orgCountryAlpha2,
      include_average_fallback: true,
    };
    if (date_start) payload.date_start = date_start;

    const modalOrigin = catalogUuidFromUsageField(item?.originlocation);
    const modalFinal = catalogUuidFromUsageField(item?.finallocation);
    const modalPrice = catalogUuidFromUsageField(item?.price);
    if (includeClarifyReplies) {
      const cr = _.pickBy(clarifyReplies, (v) => v != null && String(v).trim() !== '');
      if (Object.keys(cr).length > 0) payload.clarify_replies = cr;
      const locOverrides = clarifyCatalogOverridesFromAllReplies(clarifyReplies, clarifyItemKindByIdRef.current);
      if (locOverrides.originlocation) payload.originlocation = locOverrides.originlocation;
      else if (modalOrigin) payload.originlocation = modalOrigin;
      if (locOverrides.finallocation) payload.finallocation = locOverrides.finallocation;
      else if (modalFinal) payload.finallocation = modalFinal;
      if (modalPrice) payload.price = modalPrice;
      if (factorSuggestResponse != null && factorSuggestResponse.clarify_round != null && factorSuggestResponse.clarify_round !== '') {
        const rn = Number(factorSuggestResponse.clarify_round);
        if (!Number.isNaN(rn)) payload.clarify_round = rn;
      }
    } else {
      if (modalOrigin) payload.originlocation = modalOrigin;
      if (modalFinal) payload.finallocation = modalFinal;
      if (modalPrice) payload.price = modalPrice;
    }

    try {
      const res = await suggestFactorFromText(payload);
      if (res?.data) {
        setFactorSuggestResponse(res.data);
        setAlternatesOpen(Boolean(res.data.needs_review));
      } else {
        setFactorSuggestResponse(null);
      }
    } finally {
      setFactorSuggestRequest(null);
    }
  };

  const handleApplyFactorCandidate = async (candidate) => {
    if (!candidate || !item?.dateEnd) return;
    const snap = factorSuggestResponse;
    const textTrim = activitySuggestText.trim();
    const cr = _.pickBy(clarifyReplies, (v) => v != null && String(v).trim() !== '');
    const result = await applyFactorSuggestionToModal(candidate, {
      dateEnd: formatISO(new Date(item.dateEnd)),
      suggestResponse: snap,
    });
    if (result?.success) {
      if (snap?.usage_derivation != null) {
        editUsageModal(snap.usage_derivation, { name: 'usage_derivation' });
      }
      if (snap?.ai_assisted != null) {
        editUsageModal(snap.ai_assisted, { name: 'ai_assisted' });
      }
      if (candidate?.factor_id && textTrim) {
        const alternatesShown = snap?.alternates?.map((a) => a.factor_id).filter(Boolean);
        await submitFactorSuggestFeedback({
          text: textTrim,
          factor_id: candidate.factor_id,
          factor_name: candidate.factor_name,
          org_country: orgCountryAlpha2,
          clarify_replies: Object.keys(cr).length > 0 ? cr : undefined,
          remember_choice: rememberChoice,
          alternates_shown: alternatesShown?.length > 0 ? alternatesShown : undefined,
          confidence_score: candidate.signals?.llm_confidence ?? candidate.signals?.formula_score ?? undefined,
          clarify_rounds: snap?.clarify_round ?? undefined,
        });
      }
      setFactorSuggestApplied(candidate);
      setAlternatesOpen(false);
      if (result.usageAmount != null && !Number.isNaN(result.usageAmount)) {
        setHasUserInteractedWithUsage(true);
        setLocalTextState((s) => ({ ...s, usage: result.usageAmount }));
      }
      requestAnimationFrame(() => {
        document.getElementById('UsageData-usage')?.focus?.();
      });
    }
  };

  const isFactorSuggestClarify =
    factorSuggestResponse?.suggest_status === 'clarify' && factorSuggestClarifyRowsResolved.length > 0;
  const isFactorSuggestClarifyNoFields =
    factorSuggestResponse?.suggest_status === 'clarify' && factorSuggestClarifyRowsResolved.length === 0;
  const isFactorSuggestNoMatch = factorSuggestResponse?.suggest_status === 'no_match';
  const factorSuggestNeedsReview =
    factorSuggestResponse?.suggest_status === 'needs_review' || factorSuggestResponse?.needs_review === true;

  useEffect(() => {
    if (!isSavingUsage) return;
    if (progressState !== undefined && progressState >= 0) {
      saveProgressSeenRef.current = true;
    }
    if (progressState === 100) {
      const snap = savedUsageSnapshotRef.current;
      if (snap) {
        updateUsageYears(snap.dateStart, snap.dateEnd);
        resetUsageDataModal(snap);
      }
      savedUsageSnapshotRef.current = null;
      saveProgressSeenRef.current = false;
      setIsSavingUsage(false);
      resetFactorSuggestLocalState();
      return;
    }
    if (saveProgressSeenRef.current && progressState === undefined) {
      saveProgressSeenRef.current = false;
      savedUsageSnapshotRef.current = null;
      setIsSavingUsage(false);
    }
  }, [
    progressState,
    isSavingUsage,
    updateUsageYears,
    resetUsageDataModal,
    resetFactorSuggestLocalState,
  ]);

  // gridSources are derived from electricitySources, so the modal has to load them itself
  useEffect(() => {
    if (!electricitySources?.length) getAllItemsFromDB('electricitySources');
  }, [electricitySources?.length, getAllItemsFromDB]);

  useEffect(() => {
    if (options?.subentity?.value?.value && item?.dateEnd) {
      const subentityId = options.subentity.value.value;
      const subentityPossibleFuelMix = options.subentity.value.fuel_mix || false;
      const metric = options.metric?.value?.value;
      if (metric) getFilteredFactors(formatISO(new Date(item.dateEnd)), subentityId, subentityPossibleFuelMix, metric);
    }
  }, [
    adminPortal,
    item?.dateEnd,
    item?.dateStart,
    item?.organisation,
    getFilteredFactors,
    electricitySources?.length,
    options?.metric?.value?.value,
    options?.subentity?.value?.value,
    options?.subentity?.value?.fuel_mix,
  ]);

  useEffect(() => {
    if (item?.dateStart && item?.dateEnd) {
      const dateDiffInDays = differenceInDays(new Date(item?.dateEnd), new Date(item?.dateStart));
      const usageStartYear = new Date(item?.dateStart).getFullYear();
      const checkForEndYear = formatISO(new Date(item?.dateStart)) >= formatISO(new Date(usageStartYear, 2, 1));
      const leapYear = checkForEndYear
        ? isLeapYear(addYears(new Date(item?.dateStart), 1))
        : isLeapYear(new Date(item?.dateStart));
      const maxUsageSpanDays = leapYear ? 365 : 364;

      setInvalidUsageDate(dateDiffInDays > maxUsageSpanDays);
    }
  }, [item?.dateStart, item?.dateEnd]);

  useEffect(() => setHasFuelMix(gridSources?.length > 0), [gridSources?.length]);

  useEffect(() => {
    if (item?.id || item?.copy) {
      setHasUserInteractedWithUsage(false);
      const rawSynced = item?.copy ? '' : item?.rawUsage || item?.usage || '';
      const syncedUsage = parseUsageNumber(rawSynced) ?? rawSynced;
      setLocalTextState((state) => ({
        ...state,
        userLabel: localTextState?.userLabel || item?.userLabel || null,
        rawUsage: localTextState?.usage || item?.copy ? null : item?.rawUsage || null,
        usage: syncedUsage,
        suppliersEmissionsFactor: item?.suppliersEmissionsFactor || null,
      }));
      if (item?.fuel_mix) setLocalGridMix(item?.fuel_mix);
      // For edit mode, if the usage has fuel mix different Residual show the grid mix inputs
      if (item?.suppliersEmissionsFactor || (item?.fuel_mix && item?.fuel_mix?.['599324'] !== '100'))
        setShowFuelImport(true);
    }
  }, [item]);

  // Clear usage only when the user changes entity/subentity via dropdown (not when modal opens with a different row).
  const handleEditUsageModal = (selectedInput, event) => {
    if (event?.name === 'entity' || event?.name === 'subentity') {
      setHasUserInteractedWithUsage(true);
      setLocalTextState((state) => ({ ...state, rawUsage: null, usage: '' }));
    }
    editUsageModal(selectedInput, event);
  };

  useEffect(() => {
    if (!metric?.isSpend || item?.currency || !organisationCurrency?.id) return;

    if (item?.id || item?.copy) {
      editUsageModal(item?.rawUsageCurrency || metric?.currencyId || organisationCurrency.id, { name: 'currency' });
    } else {
      editUsageModal(organisationCurrency.id, { name: 'currency' });
    }
    editUsageModal(true, { name: 'isSpend' });
  }, [
    editUsageModal,
    metric?.currencyId,
    metric?.isSpend,
    item?.copy,
    item?.currency,
    item?.id,
    item?.rawUsageCurrency,
    organisationCurrency?.id,
  ]);

  const betaTag = feature && feature?.isBeta === true && (
    <BetaTag color={branding?.btnTextColor} backgroundColor={branding?.primaryButton} />
  );

  useEffect(() => {
    if (prevObj && JSON.stringify(prevObj) !== JSON.stringify(item)) {
      setShowPreviousDiv(true);
    } else {
      setShowPreviousDiv(false);
    }
  }, [prevObj]);

  const [buttonPosition, setButtonPosition] = useState({ top: '95%', left: '50%' });
  const handleMouseOver = () => {
    const randomTop = Math.random() * 90 + '%';
    const randomLeft = Math.random() * 90 + '%';
    setButtonPosition({ top: randomTop, left: randomLeft });
  };

  const queueUsageSaveWithSnapshot = (row) => {
    saveProgressSeenRef.current = false;
    savedUsageSnapshotRef.current = row;
    setIsSavingUsage(true);
  };
  const memoizedStartDate = useMemo(
    () => (item?.dateStart ? new Date(item.dateStart) : null),
    [item?.dateStart]
  );
  const memoizedEndDate = useMemo(
    () => (item?.dateEnd ? new Date(item.dateEnd) : null),
    [item?.dateEnd]
  );

  const maxDate = useMemo(() => new Date(), []);

  const metricsById = useMemo(() => _.keyBy(metrics || [], (metric) => String(metric.id)), [metrics]);

  const usageQuality = useMemo(
    () =>
      getUsageQuality(
        { ...item, suppliersEmissionsFactor: supplierFactor ?? item?.suppliersEmissionsFactor },
        { metricsById },
      ),
    [item, supplierFactor, metricsById],
  );

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) showUsageDataModal(false, null);
      }}>
      <DialogContent
        onClose={() => showUsageDataModal(false, null)}
        className='!flex max-h-[90vh] w-[min(100vw-1rem,56rem)] max-w-[min(100vw-1rem,56rem)] flex-col gap-0 border-border bg-card p-0 text-card-foreground shadow-xl sm:rounded-2xl'>
        <DialogHeader className='shrink-0 border-b border-border bg-card px-4 py-3 pr-14 text-left sm:px-6'>
          <div className='flex items-center gap-3'>
            <DialogTitle className='text-base font-semibold text-foreground'>
              {item?.id ? 'Edit' : 'Add'} Usage Data
            </DialogTitle>
            {usageQuality && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='inline-flex'>
                      <QualityTierBadge level={usageQuality.level} size='sm' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{usageQuality.reason}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </DialogHeader>
        <Fragment>
        <div id='modal-body' className='min-h-0 flex-1 overflow-y-auto bg-card p-0 text-[12px] text-card-foreground'>
          <div className='relative overflow-visible px-4 pb-4 pt-4 sm:px-5'>
              {(showLoading || isSavingUsage) && (
                <div
                  className='absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-4 bg-card/80 px-4'
                  style={{
                    top: 0,
                    left: 0,
                    width: '100%',
                    minHeight: '120px',
                  }}>
                  {isSavingUsage ? (
                    <>
                      <h5 className='text-center text-lg font-medium leading-normal text-foreground'>Saving your data...</h5>
                      <WaveformLoader />
                    </>
                  ) : (
                    <WaveformLoader />
                  )}
                </div>
              )}
              {showPreviousDiv && (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    paddingBottom: '10px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                  <h4 style={{ marginTop: '0' }}>
                    Data Successfully Saved! <CircleCheckBig size={16} strokeWidth={1.5} style={{ color: 'green', display: 'inline' }} />
                  </h4>
                </div>
              )}
              <div
                style={{
                  gap: '15px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  transition: 'filter 0.3s ease-in-out',
                  filter: showLoading || isSavingUsage ? 'blur(1.5px)' : 'none',
                }}>
                {/* Facility */}
                <div style={{ gridColumn: 'span 2 / span 2' }}>
                  <label style={{ display: 'inline-flex' }}>
                    Facility <em>*</em>
                  </label>
                  <CustomSelectWithTooltip
                    name='facility'
                    isClearable={true}
                    options={options?.facility?.values}
                    value={options?.facility?.value || null}
                    onChange={(s, e) => handleEditUsageModal(s?.value, e)}
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Start date *
                    {prevDates?.dateStart && (
                      <button
                        style={{
                          border: 0,
                          color: '#1f9b37',
                          paddingRight: '0',
                          background: 'inherit',
                          textDecoration: 'underline',
                        }}
                        onClick={() => editUsageModal(prevDates, { name: 'prevDates' })}>
                        Use Previous Dates
                      </button>
                    )}
                  </label>
                  <label className='input-group date datepicker'>
                    <DatePicker
                      selectsStart
                      showYearDropdown={false}
                      showMonthDropdown={false}
                      isClearable={true}
                      maxDate={maxDate}
                      id='UsageData-startDate'
                      autoComplete='off'
                      dateFormat='dd/MM/yyyy'
                      popperPlacement='bottom-end'
                      portalId={DATEPICKER_PORTAL_ID}
                      className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm startDate'
                      endDate={memoizedEndDate}
                      startDate={memoizedStartDate}
                      selected={memoizedStartDate}
                      onChange={(date, e) => {
                        const dateInput = e?.target?.value;
                        if (!date) {
                          editUsageModal(date, { name: 'dateStart' });
                        } else if (dateInput && !isValidDateFormat(dateInput)) {
                          console.log('Invalid date format');
                        } else {
                          editUsageModal(formatISO(date), { name: 'dateStart' });
                          if (!item?.dateEnd || new Date(item?.dateEnd) < date) {
                            editUsageModal(formatISO(date), { name: 'dateEnd' });
                          }
                        }
                      }}
                    />
                    <span className='input-group-btn'>
                      <Button variant='ghost' size='sm'>
                        <Calendar size={16} strokeWidth={1.5} />
                      </Button>
                    </span>
                  </label>
                </div>

                {/* End Date */}
                <div>
                  <label style={{ display: 'flex' }}>End date *</label>
                  <label className='input-group date datepicker'>
                    <DatePicker
                      selectsEnd
                      showYearDropdown={false}
                      showMonthDropdown={false}
                      isClearable={true}
                      id='UsageData-endDate'
                      autoComplete='off'
                      dateFormat='dd/MM/yyyy'
                      popperPlacement='bottom-end'
                      portalId={DATEPICKER_PORTAL_ID}
                      className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm endDate'
                      endDate={memoizedEndDate}
                      minDate={memoizedStartDate}
                      startDate={memoizedStartDate}
                      selected={memoizedEndDate}
                      onChange={(date, event) => {
                        const inputDate = event?.target?.value;
                        if (!date && !inputDate) {
                          editUsageModal(null, { name: 'dateEnd' });
                        } else if (inputDate && isValidDateFormat(inputDate)) {
                          const parsedDate = parseDateToTz(inputDate, 'dd/MM/yyyy');
                          editUsageModal(formatISO(parsedDate), { name: 'dateEnd' });
                        } else if (date) {
                          editUsageModal(formatISO(date), { name: 'dateEnd' });
                        }
                      }}
                    />
                    <span className='input-group-btn'>
                      <Button variant='ghost' size='sm'>
                        <Calendar size={16} strokeWidth={1.5} />
                      </Button>
                    </span>
                  </label>
                </div>

                {/* Invalid Date Message */}
                {invalidUsageDate && (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                    <label style={{ color: 'red', fontWeight: 'bold' }}>
                      <TriangleAlert size={20} strokeWidth={1.5} style={{ marginRight: 10, verticalAlign: 'sub', display: 'inline' }} />
                      Your date span cannot exceed 1 year.
                    </label>
                  </div>
                )}

                {/* AI-assisted usage match (factor-suggest); set REACT_APP_ENABLE_FACTOR_SUGGEST=false to hide */}
                {factorSuggestUiEnabled && (
                <div
                  style={{ gridColumn: 'span 2 / span 2' }}
                  className='rounded-lg border border-border bg-muted/30 p-4 text-foreground'>
                  <div className='mb-2 flex flex-wrap items-center gap-2'>
                    <Sparkles className='h-4 w-4 shrink-0 text-primary' strokeWidth={1.5} />
                    <span className='text-sm font-semibold text-foreground'>Describe your activity (optional)</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button type='button' variant='ghost' size='icon' className='h-7 w-7 shrink-0 cursor-help text-primary'>
                            <CircleHelp className='h-4 w-4' strokeWidth={1.5} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side='right' className='max-w-xs'>
                          <p className='text-xs'>
                            Plain-language description, e.g. &quot;Office paper waste 2 tonnes&quot;. We suggest usage type and
                            metric; you can still change anything before saving.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <textarea
                    id='UsageData-factor-suggest-text'
                    rows={3}
                    className='flex min-h-[80px] w-full rounded-lg border-[1.5px] border-input bg-transparent px-3.5 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    placeholder='e.g. Computer waste 5 tonnes, diesel for company vans…'
                    value={activitySuggestText}
                    onChange={(e) => setActivitySuggestText(e.target.value)}
                  />
                  <div className='mt-2 flex flex-wrap items-center gap-2'>
                    <Button
                      type='button'
                      variant='secondary'
                      size='sm'
                      loading={factorSuggestRequest === 'suggest'}
                      disabled={!factorSuggestPrereqsOk || !activitySuggestText.trim()}
                      onClick={() => handleFactorSuggest(false)}>
                      <Sparkles className='mr-1.5 h-3.5 w-3.5' strokeWidth={1.5} />
                      Suggest usage type
                    </Button>
                    {!factorSuggestPrereqsOk && (
                      <span className='text-xs text-muted-foreground'>
                        Set facility, start date, and end date (within one year) first.
                      </span>
                    )}
                  </div>
                  {factorSuggestResponse && (
                    <div className='mt-3 space-y-2 border-t border-border pt-3'>
                      {isFactorSuggestClarify ? (
                        <>
                          <p className='text-sm text-foreground'>
                            {factorSuggestResponse.clarify_message ||
                              factorSuggestResponse.message ||
                              'We need a bit more detail to match this activity. Answer below (you can also edit the description above), then continue.'}
                          </p>
                          {factorSuggestResponse.max_clarify_rounds != null &&
                          factorSuggestResponse.clarify_round != null &&
                          factorSuggestResponse.clarify_round !== '' ? (
                            <p className='text-xs text-muted-foreground'>
                              Clarification round {Number(factorSuggestResponse.clarify_round)} — backend allows up to{' '}
                              {Number(factorSuggestResponse.max_clarify_rounds)} round
                              {Number(factorSuggestResponse.max_clarify_rounds) === 1 ? '' : 's'} before a best-effort
                              match.
                            </p>
                          ) : null}
                          {factorSuggestResponse.clarify_context &&
                          (factorSuggestResponse.clarify_context.search_text_used ||
                            (factorSuggestResponse.clarify_context.previous_replies &&
                              typeof factorSuggestResponse.clarify_context.previous_replies === 'object' &&
                              Object.keys(factorSuggestResponse.clarify_context.previous_replies).length > 0)) ? (
                            <div className='rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground'>
                              <div className='mb-1.5 font-medium text-foreground/90'>Context carried forward</div>
                              {factorSuggestResponse.clarify_context.search_text_used ? (
                                <p className='break-words'>
                                  <span className='text-foreground/80'>Search text used: </span>
                                  {factorSuggestResponse.clarify_context.search_text_used}
                                </p>
                              ) : null}
                              {factorSuggestResponse.clarify_context.previous_replies &&
                              typeof factorSuggestResponse.clarify_context.previous_replies === 'object' &&
                              Object.keys(factorSuggestResponse.clarify_context.previous_replies).length > 0 ? (
                                <ul className='mt-2 list-inside list-disc space-y-0.5 break-words'>
                                  {Object.entries(factorSuggestResponse.clarify_context.previous_replies).map(
                                    ([k, v]) => {
                                      const kind = clarifyItemKindByIdRef.current[k];
                                      const ctx = { usageOptions, metricTypes, subentities };
                                      const raw = v != null && typeof v === 'object' ? JSON.stringify(v) : String(v);
                                      const resolved =
                                        kind && raw
                                          ? resolveClarifyOptionLabel(raw, { kind }, ctx) || raw
                                          : raw;
                                      return (
                                        <li key={k}>
                                          <span className='text-foreground/80'>{k}: </span>
                                          {resolved}
                                        </li>
                                      );
                                    },
                                  )}
                                </ul>
                              ) : null}
                            </div>
                          ) : null}
                          <div className='space-y-3'>
                            {factorSuggestClarifyRowsResolved.map((row) => {
                              const rowReply = clarifyReplies[row.key];
                              const matchedByChip = Boolean(getClarifyOptionMatch(row, rowReply));
                              return (
                                <div key={row.key} className='flex flex-col gap-1.5'>
                                  <div className='text-xs font-medium text-foreground'>
                                    {row.label}
                                    {row.kind ? (
                                      <span className='ml-1 font-normal text-muted-foreground'>({row.kind})</span>
                                    ) : null}
                                  </div>
                                  {row.options && row.options.length > 0 ? (
                                    <div className='flex flex-col gap-2'>
                                      <div className='flex flex-wrap gap-2'>
                                        {row.options.map((opt) => {
                                          const v = String(opt.value);
                                          const selected = String(clarifyReplies[row.key] ?? '') === v;
                                          return (
                                            <Button
                                              key={v}
                                              type='button'
                                              size='sm'
                                              variant={selected ? 'secondary' : 'outline'}
                                              className={selected ? 'ring-2 ring-ring' : ''}
                                              onClick={() =>
                                                setClarifyReplies((prev) => ({
                                                  ...prev,
                                                  [row.key]: selected ? '' : v,
                                                }))
                                              }>
                                              {opt.label}
                                            </Button>
                                          );
                                        })}
                                        {row.allow_free_text && !matchedByChip ? (
                                          <Button
                                            type='button'
                                            size='sm'
                                            variant='ghost'
                                            className='text-muted-foreground'
                                            onClick={() =>
                                              setClarifyReplies((prev) => ({ ...prev, [row.key]: '' }))
                                            }>
                                            Other…
                                          </Button>
                                        ) : null}
                                      </div>
                                      {row.allow_free_text && !matchedByChip ? (
                                        <Input
                                          id={`clarify-${row.id}`}
                                          className='max-w-md'
                                          value={clarifyFreeTextInputDisplay(row, rowReply)}
                                          onChange={(e) =>
                                            setClarifyReplies((prev) => ({
                                              ...prev,
                                              [row.key]: e.target.value,
                                            }))
                                          }
                                          placeholder='Type a value not in the list'
                                        />
                                      ) : null}
                                    </div>
                                  ) : (
                                    <Input
                                      id={`clarify-${row.id}`}
                                      className='max-w-md'
                                      value={clarifyReplies[row.key] ?? ''}
                                      onChange={(e) =>
                                        setClarifyReplies((prev) => ({ ...prev, [row.key]: e.target.value }))
                                      }
                                      placeholder='Your answer'
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <Button
                            type='button'
                            size='sm'
                            variant='default'
                            loading={factorSuggestRequest === 'clarify'}
                            disabled={
                              !factorSuggestClarifyRowsResolved.every((r) =>
                                isClarifyRowAnswered(r, clarifyReplies[r.key]),
                              )
                            }
                            onClick={() => handleFactorSuggest(true)}>
                            Continue with answers
                          </Button>
                        </>
                      ) : isFactorSuggestClarifyNoFields ? (
                        <>
                          <p className='text-sm text-foreground'>
                            {factorSuggestResponse.clarify_message ||
                              factorSuggestResponse.message ||
                              'More detail is needed. Update the description above and try again.'}
                          </p>
                          <Button
                            type='button'
                            size='sm'
                            variant='secondary'
                            loading={factorSuggestRequest === 'suggest'}
                            onClick={() => handleFactorSuggest(false)}>
                            Try again with updated description
                          </Button>
                        </>
                      ) : isFactorSuggestNoMatch ? (
                        <>
                          <div className='rounded-md border border-warning bg-warning-bg/50 p-3 text-sm'>
                            <div className='mb-1.5 flex items-center gap-2 font-medium text-warning'>
                              <TriangleAlert className='h-4 w-4 shrink-0' strokeWidth={1.5} />
                              No confident match found
                            </div>
                            {factorSuggestResponse.no_match_reason && (
                              <p className='text-foreground'>
                                {factorSuggestResponse.no_match_reason}
                              </p>
                            )}
                            {factorSuggestResponse.guidance && (
                              <p className='mt-1.5 text-xs text-muted-foreground'>
                                {factorSuggestResponse.guidance}
                              </p>
                            )}
                          </div>
                          {factorSuggestResponse.closest_candidates?.length > 0 && (
                            <div className='space-y-2'>
                              <div className='text-xs font-medium text-muted-foreground'>
                                Closest candidates (below confidence threshold)
                              </div>
                              {factorSuggestResponse.closest_candidates.map((c, idx) => (
                                <div
                                  key={c.factor_id || idx}
                                  className='flex flex-col gap-1 rounded-md border border-border bg-background p-2'>
                                  <div className='text-sm text-foreground'>{describeFactorCandidate(c)}</div>
                                  <Button
                                    type='button'
                                    variant='outline'
                                    size='sm'
                                    className='self-start'
                                    onClick={() => handleApplyFactorCandidate(c)}>
                                    Use this anyway
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          <Button
                            type='button'
                            size='sm'
                            variant='secondary'
                            loading={factorSuggestRequest === 'suggest'}
                            onClick={() => handleFactorSuggest(false)}>
                            Try again with different wording
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Clarify context: show what was resolved even on complete */}
                          {(() => {
                            const echoed =
                              factorSuggestResponse.clarify_context?.previous_replies &&
                              typeof factorSuggestResponse.clarify_context.previous_replies === 'object'
                                ? factorSuggestResponse.clarify_context.previous_replies
                                : null;
                            const answeredLocal = _.pickBy(
                              clarifyReplies,
                              (v) => v != null && String(v).trim() !== '',
                            );
                            const source =
                              echoed && Object.keys(echoed).length > 0
                                ? echoed
                                : Object.keys(answeredLocal).length > 0
                                  ? answeredLocal
                                  : null;
                            if (!source) return null;
                            const ctx = { usageOptions, metricTypes, subentities };
                            return (
                              <div className='rounded-md border border-border bg-muted/30 p-2.5 text-xs text-muted-foreground'>
                                <div className='mb-1 font-medium text-foreground/90'>
                                  Your answers
                                </div>
                                <ul className='list-inside list-disc space-y-0.5 break-words'>
                                  {Object.entries(source).map(([k, v]) => {
                                    const kind = clarifyItemKindByIdRef.current[k];
                                    const raw =
                                      v != null && typeof v === 'object' ? JSON.stringify(v) : String(v);
                                    const resolved =
                                      kind && raw
                                        ? resolveClarifyOptionLabel(raw, { kind }, ctx) || raw
                                        : raw;
                                    return (
                                      <li key={k}>
                                        <span className='text-foreground/80'>{k}: </span>
                                        {resolved}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            );
                          })()}

                          {factorSuggestResponse.domain?.label && (
                            <p className='text-xs text-muted-foreground'>
                              Domain: {factorSuggestResponse.domain.label}
                            </p>
                          )}
                          {factorSuggestResponse.usage_warning && (
                            <div className='flex items-start gap-2 rounded-md border border-warning bg-warning-bg/50 p-2.5 text-xs text-foreground'>
                              <TriangleAlert className='mt-0.5 h-4 w-4 shrink-0 text-warning' strokeWidth={1.5} />
                              <span>{factorSuggestResponse.usage_warning}</span>
                            </div>
                          )}
                          {factorSuggestResponse.usage_compat &&
                            factorSuggestResponse.usage_compat.compatible === false &&
                            factorSuggestResponse.usage_compat.reason && (
                              <div className='flex items-start gap-2 rounded-md border border-warning bg-warning-bg/50 p-2.5 text-xs text-foreground'>
                                <TriangleAlert className='mt-0.5 h-4 w-4 shrink-0 text-warning' strokeWidth={1.5} />
                                <span>
                                  <span className='font-medium text-foreground'>Unit check: </span>
                                  {factorSuggestResponse.usage_compat.reason}
                                  {!factorSuggestResponse.usage_in_factor_units &&
                                    factorSuggestResponse.unit_conversion?.converted_amount == null && (
                                      <span className='block pt-1 text-muted-foreground'>
                                        Enter the usage amount in the factor&apos;s units after applying, or adjust the
                                        description and try again.
                                      </span>
                                    )}
                                </span>
                              </div>
                            )}
                          {(() => {
                            if (factorSuggestApplied) return null;
                            const qtyExplain = getFactorSuggestUsageQuantityExplanation(factorSuggestResponse);
                            return qtyExplain ? (
                              <p className='text-xs text-info'>{qtyExplain}</p>
                            ) : null;
                          })()}
                          {(() => {
                            const conv = factorSuggestResponse.usage_derivation?.conversions;
                            if (!Array.isArray(conv)) return null;
                            const pkg = conv.find((c) => c.needs_user_confirmation === true);
                            if (!pkg) return null;
                            const amt = pkg.output?.amount ?? pkg.outputs?.amount;
                            const unit = pkg.output?.unit ?? pkg.outputs?.unit;
                            const desc = amt != null && unit
                              ? `We estimated ${amt} ${unit} based on standard packaging weights — please verify the quantity.`
                              : 'The quantity was estimated from packaging units — please verify.';
                            return (
                              <div className='flex items-start gap-2 rounded-md border border-warning bg-warning-bg/50 p-2.5 text-sm'>
                                <TriangleAlert className='mt-0.5 h-4 w-4 shrink-0 text-warning' strokeWidth={1.5} />
                                <span className='text-foreground'>{desc}</span>
                              </div>
                            );
                          })()}
                          {factorSuggestResponse.cache_hit === true && (
                            <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                              <span>
                                Matched from your organisation&apos;s saved preferences (no new embedding search for this
                                wording).
                              </span>
                              <Button
                                type='button'
                                variant='ghost'
                                size='sm'
                                className='h-auto shrink-0 gap-1 px-1.5 text-xs text-destructive hover:text-destructive'
                                onClick={async () => {
                                  const clearPayload = factorSuggestResponse.cache_key
                                    ? { cache_key: factorSuggestResponse.cache_key }
                                    : { text: activitySuggestText.trim() };
                                  const result = await clearFactorSuggestCache(clearPayload);
                                  if (result?.success) {
                                    setFactorSuggestResponse(null);
                                    setFactorSuggestApplied(null);
                                  }
                                }}>
                                Clear cached match
                              </Button>
                            </div>
                          )}
                          {factorSuggestNeedsReview && !factorSuggestApplied && (
                            <p className='text-xs font-medium text-warning'>
                              Please confirm a match — confidence is lower for this suggestion.
                            </p>
                          )}

                          {/* Applied banner */}
                          {factorSuggestApplied && (
                            <div className='flex items-center gap-2 rounded-md border border-success bg-success-bg/50 p-2.5'>
                              <CircleCheckBig className='h-4 w-4 shrink-0 text-success' strokeWidth={1.5} />
                              <span className='flex-1 text-sm text-foreground'>
                                Applied: {describeFactorCandidate(factorSuggestApplied)}
                              </span>
                              <Button
                                type='button'
                                variant='ghost'
                                size='sm'
                                className='h-auto shrink-0 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground'
                                onClick={() => setFactorSuggestApplied(null)}>
                                <Undo2 className='h-3.5 w-3.5' strokeWidth={1.5} />
                                Change
                              </Button>
                            </div>
                          )}

                          {/* Factor selection audit trail (kept in suggestion panel) */}
                          {factorSuggestResponse.usage_derivation?.factor_selection && (
                            <div className='rounded-md border border-border bg-card text-card-foreground'>
                              <button
                                type='button'
                                className='flex w-full items-center gap-2 p-2.5 text-left text-xs font-medium text-muted-foreground hover:text-foreground'
                                onClick={() => setDerivationOpen((o) => !o)}>
                                <FileSearch className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
                                <span className='flex-1'>Why this factor was chosen (audit trail)</span>
                                {derivationOpen
                                  ? <ChevronUp className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
                                  : <ChevronDown className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />}
                              </button>
                              {derivationOpen && (() => {
                                const fs = factorSuggestResponse.usage_derivation.factor_selection;
                                return (
                                  <div className='space-y-1.5 border-t border-border px-3 pb-3 pt-2 text-xs'>
                                    <div className='rounded border border-border bg-muted/30 p-2 space-y-1.5'>
                                      <div className='flex flex-wrap gap-x-3 gap-y-0.5'>
                                        <span>Method: <span className='font-medium text-foreground'>{fs.method}</span></span>
                                        {fs.llm_model && <span>LLM: <span className='font-medium text-foreground'>{fs.llm_model}</span></span>}
                                        {fs.embedding_model && <span>Embed: <span className='font-medium text-foreground'>{fs.embedding_model}</span></span>}
                                        {fs.candidates_considered != null && (
                                          <span>Candidates: <span className='font-medium text-foreground'>{fs.candidates_considered}</span></span>
                                        )}
                                      </div>
                                      {fs.selected && (
                                        <div className='space-y-0.5'>
                                          <div className='text-foreground'>
                                            Selected: <span className='font-medium'>{fs.selected.factor_name || fs.selected.factor_id}</span>
                                            {fs.selected.confidence != null && (
                                              <span className='ml-1.5 text-muted-foreground'>(confidence: {fs.selected.confidence})</span>
                                            )}
                                          </div>
                                          {fs.selected.reasoning && (
                                            <div className='text-muted-foreground leading-snug'>{fs.selected.reasoning}</div>
                                          )}
                                        </div>
                                      )}
                                      {Array.isArray(fs.top_candidates) && fs.top_candidates.length > 0 && (
                                        <div className='mt-1'>
                                          <div className='mb-0.5 text-muted-foreground'>Candidates evaluated:</div>
                                          <ul className='list-inside list-disc space-y-0.5'>
                                            {fs.top_candidates.map((tc, ti) => (
                                              <li key={tc.factor_id || ti} className='text-foreground'>
                                                {tc.factor_name || tc.factor_id}
                                                {tc.cosine_sim != null && (
                                                  <span className='ml-1.5 text-muted-foreground'>(similarity: {tc.cosine_sim})</span>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {fs.formula_score != null && (
                                        <div className='text-muted-foreground'>
                                          Formula cross-check score: {fs.formula_score}
                                        </div>
                                      )}
                                    </div>
                                    <div className='text-[10px] text-muted-foreground'>
                                      Schema: {factorSuggestResponse.usage_derivation.schema_version || 'unknown'}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {/* Remember-choice toggle */}
                          {(factorSuggestResponse.best || factorSuggestResponse.alternates?.length > 0) && !factorSuggestApplied && (
                            <label className='flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none'>
                              <input
                                type='checkbox'
                                checked={rememberChoice}
                                onChange={(e) => setRememberChoice(e.target.checked)}
                                className='h-3.5 w-3.5 rounded border-border accent-primary'
                              />
                              Remember this choice for future matching
                            </label>
                          )}

                          {/* Best match */}
                          {factorSuggestResponse.best && !factorSuggestApplied && (
                            <div className='flex flex-col gap-2 rounded-md border border-border bg-card p-3'>
                              <div className='text-xs text-muted-foreground'>Top match</div>
                              <div className='text-sm text-card-foreground'>
                                {describeFactorCandidate(factorSuggestResponse.best)}
                              </div>
                              <Button
                                type='button'
                                size='sm'
                                className='self-start'
                                onClick={() => handleApplyFactorCandidate(factorSuggestResponse.best)}>
                                Use this suggestion
                              </Button>
                            </div>
                          )}

                          {/* Alternates — collapsible */}
                          {factorSuggestResponse.alternates?.length > 0 && !factorSuggestApplied && (
                            <div className='space-y-2'>
                              <button
                                type='button'
                                className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground'
                                onClick={() => setAlternatesOpen((o) => !o)}>
                                {alternatesOpen ? (
                                  <ChevronUp className='h-3.5 w-3.5' strokeWidth={1.5} />
                                ) : (
                                  <ChevronDown className='h-3.5 w-3.5' strokeWidth={1.5} />
                                )}
                                Other options ({factorSuggestResponse.alternates.length})
                              </button>
                              {alternatesOpen &&
                                factorSuggestResponse.alternates.map((alt, idx) => (
                                  <div
                                    key={alt.factor_id || alt.combo_id || idx}
                                    className='flex flex-col gap-1 rounded-md border border-border bg-background p-2'>
                                    <div className='text-sm text-foreground'>{describeFactorCandidate(alt)}</div>
                                    <Button
                                      type='button'
                                      variant='outline'
                                      size='sm'
                                      className='self-start'
                                      onClick={() => handleApplyFactorCandidate(alt)}>
                                      Use this instead
                                    </Button>
                                  </div>
                                ))}
                            </div>
                          )}

                          {/* Re-pick after applied */}
                          {factorSuggestApplied && factorSuggestResponse.alternates?.length > 0 && (
                            <div className='space-y-2'>
                              <button
                                type='button'
                                className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground'
                                onClick={() => {
                                  setFactorSuggestApplied(null);
                                  setAlternatesOpen(true);
                                }}>
                                <Undo2 className='h-3.5 w-3.5' strokeWidth={1.5} />
                                See all suggestions and pick a different one
                              </button>
                            </div>
                          )}

                          {!factorSuggestResponse.best &&
                            factorSuggestResponse.suggest_status !== 'clarify' &&
                            factorSuggestResponse.suggest_status !== 'no_match' && (
                            <p className='text-sm text-muted-foreground'>
                              No suggestion returned. Try different wording or choose usage type manually.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Custom Top-Level Category */}
                <div style={{ gridColumn: 'span 2 / span 2' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Custom top-level category
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CircleHelp size={16} strokeWidth={1.5} style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }} />
                        </TooltipTrigger>
                        <TooltipContent side='right'>
                          <span className='text-xs text-popover-foreground'>Optional customised top-level category name.</span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </label>
                  <Input
                    name='userLabel'
                    id='UsageData-userLabel'
                    value={localTextState?.userLabel || ''}
                    onChange={(e) => setLocalTextState((state) => ({ ...state, userLabel: e?.target?.value || null }))}
                  />
                </div>

                {/* Usage Type */}
                <div style={{ gridColumn: 'span 2 / span 2' }}>
                  <label style={{ display: 'inline-flex' }}>
                    Usage type <em>*</em>
                  </label>
                  <CustomSelectWithTooltip
                    zIndex='auto'
                    name='entity'
                    isClearable={true}
                    options={options?.entity?.values}
                    value={options?.entity?.value || null}
                    onChange={(s, e) => {
                      handleEditUsageModal(s?.value, e);
                      getUsageOptions(s?.value);
                    }}
                  />
                </div>

                {/* Dynamic Usage Options Fields */}
                {item?.dateStart &&
                  _.sortBy(Object.entries(options), ([key]) => orderPriority[key] || Infinity)
                    .filter(
                      ([key]) =>
                        options[key]?.values?.length > 0 &&
                        !['facility', 'entity'].includes(key) &&
                        !(key === 'metric' && options?.metric?.values?.length === 1)
                    )
                    .map(([key, option]) => (
                      <div key={key} style={{ gridColumn: 'span 2 / span 2' }}>
                        <label>
                          {option.title} <em>*</em>
                        </label>
                        <CustomSelectWithTooltip
                          name={key}
                          item={item}
                          zIndex='auto'
                          isClearable={true}
                          options={option.values}
                          value={option.value || null}
                          editUsageModal={handleEditUsageModal}
                          usageTypeDetails={usageTypeDetails}
                          onChange={(s, e) => handleEditUsageModal(key === 'subentity' ? s : s?.value, e)}
                        />
                      </div>
                    ))}

                {/* Usage Number */}
                {item?.dateStart && item?.metric && (
                  <div style={{ gridColumn: 'span 2 / span 2' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                      Usage in&nbsp;
                      {metric?.isSpend ? `${currency?.symbol} (${currency?.isoCode})` : options.metric?.value?.label}
                      &nbsp;
                      <em>*</em>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <CircleHelp size={16} strokeWidth={1.5} style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }} />
                          </TooltipTrigger>
                          <TooltipContent side='right'>
                            <span className='text-xs text-popover-foreground'>
                              {metric?.isSpend
                                ? `Amount of ${currency?.symbol} (${currency?.isoCode}) you spent on usage type.`
                                : options?.metric?.value?.description || ''}
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <div className='input-container' style={{ position: 'relative' }}>
                      <NumericFormat
                        name={'numberName'}
                        id='UsageData-usage'
                        inputMode='numeric'
                        allowNegative={false}
                        className='flex h-9 w-full rounded-lg border-[1.5px] border-input bg-transparent px-3.5 py-2 text-sm'
                        value={hasUserInteractedWithUsage ? localTextState.usage : (localTextState.usage ?? item?.rawUsage ?? item?.usage ?? '')}
                        onValueChange={(e) => {
                        setHasUserInteractedWithUsage(true);
                        setLocalTextState((state) => ({ ...state, usage: e.floatValue || null }));
                      }}
                        isAllowed={(values) => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue < 1000000000000;
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          opacity: 0.4,
                          pointerEvents: 'none',
                        }}>
                        {metric?.isSpend ? `${currency?.symbol}` : options.metric?.value?.label}
                      </span>
                    </div>
                  </div>
                )}

                {/* Quantity audit: usage_derivation.conversions (paper, flights, unit_normalization, …); legacy unit_conversion-only fallback */}
                {factorSuggestApplied &&
                  (hasUsageDerivationQuantityConversions(factorSuggestResponse) ||
                    hasUnitConversionWithoutDerivationMirror(factorSuggestResponse)) && (
                  <div style={{ gridColumn: 'span 2 / span 2' }}>
                    <div className='rounded-md border border-border bg-card text-card-foreground'>
                      <button
                        type='button'
                        className='flex w-full items-center gap-2 p-2.5 text-left text-xs font-medium text-muted-foreground hover:text-foreground'
                        onClick={() => setConversionOpen((o) => !o)}>
                        <FileSearch className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
                        <span className='flex-1'>How this quantity was calculated</span>
                        {conversionOpen
                          ? <ChevronUp className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
                          : <ChevronDown className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />}
                      </button>
                      {conversionOpen && (() => {
                        const d = factorSuggestResponse.usage_derivation;
                        const conversions = Array.isArray(d?.conversions) ? d.conversions : [];
                        const uc = factorSuggestResponse.unit_conversion;
                        const showDeriv = conversions.length > 0;
                        const showUcFallback = hasUnitConversionWithoutDerivationMirror(factorSuggestResponse);
                        const inputQuote =
                          d?.inputs?.description_text ||
                          (!d?.inputs?.description_text &&
                          activitySuggestText.trim() &&
                          (showUcFallback || derivationsIncludeUnitNormalization(factorSuggestResponse))
                            ? activitySuggestText.trim()
                            : null);
                        return (
                          <div className='space-y-2 border-t border-border px-3 pb-3 pt-2 text-xs'>
                            {inputQuote && (
                              <div className='italic text-muted-foreground'>
                                &ldquo;{inputQuote}&rdquo;
                              </div>
                            )}
                            {showDeriv &&
                              conversions.map((conv, ci) => (
                                <div key={conv.rule_id || ci} className='space-y-1 rounded border border-border bg-muted/30 p-2'>
                                  <div className='flex flex-wrap items-baseline gap-x-2'>
                                    <span className='font-medium text-foreground'>{conv.rule_id}</span>
                                    {conv.rule_version && (
                                      <span className='text-muted-foreground'>v{conv.rule_version}</span>
                                    )}
                                  </div>
                                  {conv.constants_ref && (
                                    <div className='text-muted-foreground leading-snug'>{conv.constants_ref}</div>
                                  )}
                                  {Array.isArray(conv.steps) && conv.steps.length > 0 && (
                                    <ol className='mt-1 list-inside list-decimal space-y-0.5 text-foreground'>
                                      {conv.steps.map((s, si) => (
                                        <li key={si}>
                                          <span className='font-medium'>{s.step}</span>
                                          {s.detail && <span className='text-muted-foreground'> — {s.detail}</span>}
                                        </li>
                                      ))}
                                    </ol>
                                  )}
                                  {(conv.output || conv.outputs) && (() => {
                                    const out = conv.output || conv.outputs;
                                    return (
                                      <div className='mt-1 font-medium text-foreground'>
                                        Result: {out.amount} {out.unit}
                                      </div>
                                    );
                                  })()}
                                  {conv.needs_user_confirmation && (
                                    <div className='mt-1 flex items-center gap-1.5 text-warning'>
                                      <TriangleAlert className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
                                      Estimated — please verify this quantity
                                    </div>
                                  )}
                                </div>
                              ))}
                            {showUcFallback && uc && (
                              <div className='space-y-1 rounded border border-border bg-muted/30 p-2'>
                                <div className='flex flex-wrap items-baseline gap-x-2'>
                                  <span className='font-medium text-foreground'>unit_normalization</span>
                                  <span className='text-muted-foreground'>cyf-agents unitNormalizer</span>
                                </div>
                                {uc.message && String(uc.message).trim() && (
                                  <p className='text-foreground leading-snug'>{String(uc.message).trim()}</p>
                                )}
                                {uc.description && String(uc.description).trim() && uc.description !== uc.message && (
                                  <p className='text-muted-foreground leading-snug'>{String(uc.description).trim()}</p>
                                )}
                                {!uc.message &&
                                  !uc.description &&
                                  uc.from_unit &&
                                  uc.to_unit &&
                                  (() => {
                                    const factorNum =
                                      uc.factor !== undefined && uc.factor !== null && uc.factor !== ''
                                        ? Number(uc.factor)
                                        : NaN;
                                    const convNum =
                                      uc.converted_amount !== undefined &&
                                      uc.converted_amount !== null &&
                                      uc.converted_amount !== ''
                                        ? Number(uc.converted_amount)
                                        : NaN;
                                    const parts = [`Convert ${uc.from_unit} → ${uc.to_unit}.`];
                                    if (!Number.isNaN(factorNum)) {
                                      parts.push(`Factor: ${factorNum}.`);
                                    }
                                    if (!Number.isNaN(convNum)) {
                                      parts.push(`Result: ${convNum} ${uc.to_unit}.`);
                                    }
                                    return <p className='text-foreground leading-snug'>{parts.join(' ')}</p>;
                                  })()}
                                {factorSuggestResponse.usage?.amount != null &&
                                  factorSuggestResponse.usage.amount !== '' && (
                                    <div className='mt-1 font-medium text-foreground'>
                                      Input quantity: {factorSuggestResponse.usage.amount}
                                      {factorSuggestResponse.usage.unit
                                        ? ` ${factorSuggestResponse.usage.unit}`
                                        : ''}
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Water / Solar Usage */}
                {(waterTreatment || solarConsumed) && (
                  <div style={{ gridColumn: 'span 2 / span 2' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {waterTreatment ? 'Water Treatment in' : 'Solar Consumed in'} {options?.metric?.value?.label}
                      &nbsp;
                      <em>*</em>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <CircleHelp size={16} strokeWidth={1.5} style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }} />
                          </TooltipTrigger>
                          <TooltipContent side='right'>
                            <span className='text-xs text-popover-foreground'>{options?.metric?.value?.description || ''}</span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <NumericFormat
                      inputMode='numeric'
                      allowNegative={false}
                      className='flex h-9 w-full rounded-lg border-[1.5px] border-input bg-transparent px-3.5 py-2 text-sm'
                      name={waterTreatment ? 'waterTreatment' : 'solarConsumed'}
                      value={localTextState[waterTreatment ? 'waterTreatment' : 'solarConsumed']}
                      onValueChange={(values) =>
                        setLocalTextState((state) => ({
                          ...state,
                          [waterTreatment ? 'waterTreatment' : 'solarConsumed']: values.floatValue ?? null,
                        }))
                      }
                    />
                  </div>
                )}

                {/* Energy Fuel Mix */}
                {item?.dateStart && item?.entity && item?.subentity && options?.subentity?.value?.fuel_mix && (
                  <>
                    {/* Grid Toggle */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gridColumn: 'span 2 / span 2',
                        justifyContent: 'space-between',
                      }}>
                      <label style={{ display: 'inline-flex', marginBottom: '0', alignItems: 'center' }}>
                        Do you have your supplier's emission factor{gridSources?.length > 0 ? ' or fuel mix' : ''}?
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <CircleHelp size={16} strokeWidth={1.5} style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }} />
                            </TooltipTrigger>
                            <TooltipContent side='top' className='z-[99999]'>
                              <span className='text-xs text-popover-foreground'>
                                {
                                  'Please enter your electricity emissions factor and/or fuel mix. You can find this information on your energy provider’s website or in your utility bills. If entering a fuel mix, enter this as a percentage and ensure it totals to 100%. If entering an emissions factor, note that Scope 3 emissions will also be calculated automatically.'
                                }
                              </span>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </label>
                      <div style={{ gap: '1rem', display: 'flex' }}>
                        <Button
                          disabled={showFuelImport}
                          type='button'
                          className='hover:bg-success/90'
                          id='UsageData-fuelMix-yes'
                          onClick={() => {
                            if (gridSources?.length > 0) {
                              setLocalGridMix(Object.fromEntries(gridSources.map((str) => [str, '0'])));
                            }
                            setShowFuelImport(true);
                            setLocalTextState((state) => ({ ...state, suppliersEmissionsFactor: null }));
                          }}>
                          Yes
                        </Button>
                        <Button
                          type='button'     
                          className='bg-warning text-warning hover:bg-warning/90'
                          id='UsageData-fuelMix-no'
                          onClick={() => {
                            setLocalGridMix({ 599324: '100' });
                            setShowFuelImport(false);
                          }}>
                          No
                        </Button>
                      </div>
                    </div>

                    {/* Supplier Factor */}
                    {showFuelImport && (
                      <div style={{ gridColumn: 'span 2 / span 2' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                          Supplier Factor (Scope 2 Only)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <CircleHelp size={16} strokeWidth={1.5} style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }} />
                              </TooltipTrigger>
                              <TooltipContent side='right'>
                                <span className='text-xs text-popover-foreground'>
                                  {
                                    "You will find your energy providers' emission factor via their website or in your utility bills. Enter the value of the emissions factor in kgCO2e / kWh."
                                  }
                                </span>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </label>
                        <div className='input-container' style={{ position: 'relative' }}>
                          <NumericFormat
                            name={'suppliersEmissionsFactor'}
                            id='UsageData-suppliersEmissionsFactor'
                            inputMode='numeric'
                            allowNegative={false}
                            className='flex h-9 w-full rounded-lg border-[1.5px] border-input bg-transparent px-3.5 py-2 text-sm'
                            value={localTextState.suppliersEmissionsFactor}
                            onValueChange={(values) =>
                              setLocalTextState((state) => ({
                                ...state,
                                suppliersEmissionsFactor: values.floatValue || null,
                              }))
                            }
                            isAllowed={(values) => {
                              const { floatValue } = values;
                              return floatValue === undefined || floatValue < 1000000000000;
                            }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              opacity: 0.4,
                              pointerEvents: 'none',
                            }}>
                            kgCO2e/kWh
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Grid Mix for other sources */}
                    {showFuelImport &&
                      gridSources?.length > 0 &&
                      gridSources
                        .filter((gs) => gs !== '599324')
                        .map((gs, index) => (
                          <div
                            key={index}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{ maxWidth: '50%' }}>{_.find(electricitySources, { id: gs })?.source}</label>
                            <NumericFormat
                              name={gs}
                              defaultValue={0}
                              inputMode='numeric'
                              allowNegative={false}
                              style={{ width: '50%' }}
                              value={localGridMix?.[gs]}
                              isAllowed={(values) => {
                                if (!values.value) return true;
                                const { floatValue } = values;
                                return floatValue < 101;
                              }}
                              className='flex h-9 w-full rounded-lg border-[1.5px] border-input bg-transparent px-3.5 py-2 text-sm'
                              id={`UsageData-fuelMix-${gs}`}
                              onChange={(e) => localGridChange(e.target.value || 0, gs)}
                            />
                          </div>
                        ))}

                    {showFuelImport &&
                      localGridMix?.['599324'] !== '100' &&
                      ((fuelMixSum !== 100 && fuelMixSum !== 0 && localTextState?.suppliersEmissionsFactor != null) ||
                        (fuelMixSum !== 100 && localTextState?.suppliersEmissionsFactor == null)) && (
                        <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                          <label style={{ color: 'red', verticalAlign: 'sub', fontWeight: 'bold' }}>
                            <TriangleAlert size={20} strokeWidth={1.5} style={{ marginRight: 10, display: 'inline' }} />
                            Grid mix does not make up 100% or missing the supplier factor.
                          </label>
                        </div>
                      )}
                  </>
                )}

                {/* Editing Solar Usage */}
                {item?.id && solarSubentities?.includes(item?.subentity) && (
                  <div
                    className='alert alert-info'
                    style={{ color: '#130384', marginBottom: '0', gridColumn: 'span 2 / span 2' }}>
                    <TriangleAlert size={16} strokeWidth={1.5} style={{ display: 'inline' }} />
                    &nbsp; When editing Solar usage, ensure that your corresponding data for the same time periods fits this
                    formula:
                    <p style={{ margin: 'auto', width: 'fit-content' }}>
                      Solar Generated kWh = Solar Consumed kWh + Solar Exported kWh
                    </p>
                  </div>
                )}

                {/* No Carbon Factor Message */}
                {item?.dateStart && conversionFactors?.length === 0 && (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                    <label style={{ color: 'red', fontWeight: 'bold' }}>
                      <TriangleAlert size={20} strokeWidth={1.5} style={{ marginRight: 10, verticalAlign: 'sub', display: 'inline' }} />
                      Sorry, we don't have a carbon factor for that year, please contact support for help.
                    </label>
                  </div>
                )}

                {/* Submit Button */}
                {parseFloat(localTextState?.usage) > 0 && hasAprilFools && aprilFools?.users?.includes(user) ? (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto', marginBottom: '32px' }}>
                    <Button
                      id='submit-usage'
                      style={{
                        position: 'absolute',
                        top: buttonPosition.top,
                        left: buttonPosition.left,
                        transform: 'translate(-50%, -50%)',
                        zIndex: '999',
                      }}
                      onMouseOver={handleMouseOver}
                      onClick={() => {
                        if (canSubmitUsage) {
                          const row = {
                            ...item,
                            usage: localTextState?.usage,
                            waterTreatment: localTextState?.waterTreatment,
                            solarConsumed: localTextState?.solarConsumed,
                            userLabel: localTextState?.userLabel,
                            fuel_mix: (hasFuelMix && localGridMix) || null,
                            suppliersEmissionsFactor: supplierFactor,
                            useSupplierCarbonFactor: supplierFactor !== null ? 'Yes' : 'No',
                          };
                          queueUsageSaveWithSnapshot(row);
                          saveUsageData([row], adminPortal);
                          setHasFuelMix(false);
                          setLocalGridMix(false);
                          setLocalTextState({
                            usage: null,
                            userLabel: null,
                            waterTreatment: null,
                            solarConsumed: null,
                          });
                          setSupplierFactor(null);
                        }
                      }}>
                      {canSubmitUsage ? `${item?.id ? 'Edit' : 'Add'} Data` : 'Data Incomplete'}
                    </Button>
                  </div>
                ) : (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                    <Button
                      id='submit-usage'
                      style={{ cursor: !canSubmitUsage && 'not-allowed' }}
                      onClick={() => {
                        if (canSubmitUsage) {
                          const row = {
                            ...item,
                            usage: localTextState?.usage,
                            waterTreatment: localTextState?.waterTreatment,
                            solarConsumed: localTextState?.solarConsumed,
                            userLabel: localTextState?.userLabel,
                            fuel_mix: hasFuelMix && fuelMixSum === 100 ? localGridMix : { 599324: '100' },
                            suppliersEmissionsFactor: localTextState?.suppliersEmissionsFactor || null,
                          };
                          queueUsageSaveWithSnapshot(row);
                          saveUsageData([row], adminPortal);
                          setHasFuelMix(false);
                          setLocalGridMix(false);
                          setLocalTextState({
                            usage: null,
                            userLabel: null,
                            waterTreatment: null,
                            solarConsumed: null,
                            suppliersEmissionsFactor: null,
                          });
                          setSupplierFactor(null);
                        }
                      }}>
                      {canSubmitUsage ? `${item?.id ? 'Edit' : 'Add'} Data` : 'Data Incomplete'}
                    </Button>
                  </div>
                )}

                {!item?.id && showButton && factorDetails && (
                  <>
                    <div style={{ gridColumn: 'span 2 / span 2' }}>
                      <hr className='border-border my-4' style={{ borderColor: '#AAA' }} />
                    </div>
                    <div
                      style={{
                        gridColumn: 'span 2 / span 2',
                        margin: 'auto',
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-evenly',
                      }}>
                      <Button
                        variant='outline'
                        onClick={() => setShowFactorDetails(!showFactorDetails)}
                        style={{
                          border: `2px solid ${branding?.primaryButton}`,
                          fontSize: '14px',
                          fontWeight: '600',
                          borderRadius: '6px',
                          padding: '5px',
                          backgroundColor: '#F9F9F9',
                          color: '#1A1A1A',
                          fontFamily: font,
                        }}>
                        {showFactorDetails ? 'Hide' : 'See Emissions Factor Details'}
                        {betaTag}
                      </Button>
                    </div>
                  </>
                )}

                {/* Admin and More Details */}
                {item?.id && showButton && (
                  <>
                    <div
                      style={{ gridColumn: 'span 2 / span 2', display: factorDetails || cyfDataAdmin ? 'block' : 'none' }}>
                      <hr className='border-border my-4' style={{ borderColor: '#AAA' }} />
                    </div>
                    <div
                      style={{
                        gridColumn: 'span 2 / span 2',
                        margin: 'auto',
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-evenly',
                      }}>
                      <div style={{ display: cyfDataAdmin ? 'flex' : 'none' }}>
                        <Button
                          variant='outline'
                          onClick={() => {
                            if (!usageDetails) getUsageById(item?.id);
                            setShowMore(!showMore);
                            if (!showMore === true) setShowFactorDetails(false);
                          }}
                          style={{
                            border: `2px solid ${branding?.primaryButton}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '6px',
                            padding: '5px',
                            backgroundColor: '#F9F9F9',
                            color: '#1A1A1A',
                            fontFamily: font,
                          }}>
                          {showMore ? 'Hide' : 'See More'}
                        </Button>
                      </div>
                      <div style={{ display: factorDetails ? 'flex' : 'none' }}>
                        <Button
                          variant='outline'
                          onClick={() => {
                            if (!usageDetails) getUsageById(item?.id);
                            setShowFactorDetails(!showFactorDetails);
                            if (!showFactorDetails === true) setShowMore(false);
                          }}
                          style={{
                            border: `2px solid ${branding?.primaryButton}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '6px',
                            padding: '5px',
                            backgroundColor: '#F9F9F9',
                            color: '#1A1A1A',
                            fontFamily: font,
                          }}>
                          {showFactorDetails ? 'Hide' : 'See Emissions Factor Details'}
                          {betaTag}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
                {showMore && usageDetails && cyfDataAdmin && item?.id && (
                  <div style={{ overflowWrap: 'break-word', gridColumn: 'span 2 / span 2' }}>
                    <table>
                      <tbody>
                        <tr key='modifiedDate'>
                          <td>
                            <p>
                              <b>Modified Date:</b> {usageDetails?.modifiedDate}
                            </p>
                            <p>
                              <b>Usage Data End Date:</b> {usageDetails?.dateEnd}
                            </p>
                          </td>
                        </tr>
                        {['location', 'market'].map((el, key) => (
                          <>
                            <tr key={key}>
                              <td>
                                <p style={{ fontWeight: 'bold' }}>
                                  {el.charAt(0).toUpperCase() + el.slice(1)}:
                                  <button
                                    onClick={() => {
                                      el === 'location' ? toggleLocationVisibility() : toggleMarketVisibility();
                                    }}
                                    style={{ marginLeft: 5, border: 0, backgroundColor: 'inherit' }}>
                                    {el === 'location' ? (isLocationVisible ? '▼' : '▶') : isMarketVisible ? '▼' : '▶'}
                                  </button>
                                </p>
                              </td>
                            </tr>
                            {(el === 'location' ? isLocationVisible : isMarketVisible) &&
                              ['calendarYear', 'reportingYear'].map((elem, index) => (
                                <>
                                  <tr key={index}>
                                    <td>
                                      <p style={{ fontWeight: 'bold' }}>{elem.charAt(0).toUpperCase() + elem.slice(1)}</p>
                                    </td>
                                  </tr>
                                  <tr key={`object-${index}`}>
                                    <td>
                                      {Object.keys(usageDetails?.[el]?.[elem] || {}).map((x, i) =>
                                        x !== 'conversionFactors' ? (
                                          <ol
                                            key={i}
                                            style={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              paddingLeft: 20,
                                              marginBottom: 5,
                                            }}>
                                            <p style={{ marginRight: 5 }}>
                                              <b>{x === 'year' ? 'Ideal Factor Year' : x === 'endDate' ? 'RYE' : x}</b>:
                                            </p>{' '}
                                            {typeof usageDetails?.[el]?.[elem]?.[x] === 'object' &&
                                            usageDetails?.[el]?.[elem]?.[x] !== null ? (
                                              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                {Object.entries(usageDetails?.[el]?.[elem]?.[x] || {}).map(
                                                  ([key, value]) => (
                                                    <div key={key} style={{ marginRight: '10px' }}>
                                                      <strong>{key}:</strong> {JSON.stringify(value)}
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            ) : (
                                              JSON.stringify(usageDetails?.[el]?.[elem]?.[x])
                                            )}
                                          </ol>
                                        ) : (
                                          Object.entries(usageDetails?.[el]?.[elem]?.[x]?.[0] || {}).map(
                                            ([key, value]) =>
                                              key === 'year' && (
                                                <ol
                                                  key={key}
                                                  style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    paddingLeft: 20,
                                                    marginBottom: 5,
                                                  }}>
                                                  <p style={{ marginRight: 5 }}>
                                                    <strong>Chosen Factor Year:</strong> {value}
                                                  </p>
                                                </ol>
                                              )
                                          )
                                        )
                                      )}
                                    </td>
                                  </tr>
                                </>
                              ))}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {showFactorDetails && usageDetails && factorDetails && item?.id && showButton && (
                  <div
                    style={{
                      display: 'flex',
                      padding: 10,
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gridColumn: 'auto / span 2',
                      width: '95%',
                      justifySelf: 'center',
                    }}>
                    <div>
                      <h4 style={{ display: 'flex', marginTop: 5, justifyContent: 'center', fontWeight: 'bold' }}>
                        Emissions Factor Details:
                      </h4>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        overflowWrap: 'break-word',
                        gridColumn: 'span 2 / span 2',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}>
                      {factors?.map((factor, index) => (
                        <div
                          key={`factor-${index}`}
                          style={{
                            display: 'flex',
                            border: '1px solid #CCC',
                            borderRadius: '15px',
                            padding: 10,
                            justifyContent: 'center',
                            flexDirection: 'column',
                            marginBottom: 10,
                          }}>
                          <table className='app-table' style={{ width: '100%' }}>
                            <tbody>
                              {Array.from(seeDetails.keys())
                                .filter((d) => d !== 'Metric')
                                .map((detail, key) => {
                                  const value = seeDetails.get(detail)(index);
                                  return (
                                    <tr key={key} style={{ display: value ? 'table-row' : 'none' }}>
                                      <td style={{ fontWeight: 'bold', borderTop: key === 0 ? 0 : '1px solid #DDD' }}>
                                        {detail}:
                                      </td>
                                      <td style={{ borderTop: key === 0 ? 0 : '1px solid #DDD' }}>{value}</td>
                                    </tr>
                                  );
                                })}
                              <tr key={`emissionsFactors-${index}`}>
                                <td colSpan='2' style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                  Emissions Factors
                                </td>
                              </tr>
                              <tr key={`Scope-${index}`}>
                                <td colSpan='2'>
                                  <span>
                                    <b>Scope {factor?.scope}:</b> {factor?.co2e} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                              <tr key={`Scope3WTT-${index}`}>
                                <td colSpan='2' style={{ display: factor?.wtt ? 'table-cell' : 'none' }}>
                                  <span>
                                    <b>Scope 3 WTT:</b> {factor?.wtt} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {showFactorDetails && factorDetails && !item?.id && showButton && (
                  <div
                    style={{
                      display: 'flex',
                      padding: 10,
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gridColumn: 'auto / span 2',
                      width: '95%',
                      justifySelf: 'center',
                    }}>
                    <div>
                      <h4 style={{ display: 'flex', marginTop: 5, justifyContent: 'center', fontWeight: 'bold' }}>
                        Emissions Factor Details:
                      </h4>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        overflowWrap: 'break-word',
                        gridColumn: 'span 2 / span 2',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}>
                      {factors?.map((factor, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            border: '1px solid #CCC',
                            borderRadius: '15px',
                            padding: 10,
                            justifyContent: 'center',
                            flexDirection: 'column',
                            marginBottom: 10,
                          }}>
                          <table className='app-table' style={{ width: '100%' }}>
                            <tbody>
                              {Array.from(seeDetails.keys())
                                .filter((d) => d !== 'Metric')
                                .map((detail, key) => {
                                  const value = seeDetails.get(detail)(index);
                                  return (
                                    <tr key={key} style={{ display: value ? 'table-row' : 'none' }}>
                                      <td style={{ fontWeight: 'bold', borderTop: key === 0 ? 0 : '1px solid #DDD' }}>
                                        {detail}:
                                      </td>
                                      <td style={{ borderTop: key === 0 ? 0 : '1px solid #DDD' }}>{value}</td>
                                    </tr>
                                  );
                                })}
                              <tr key={`emissionsFactors-${index}`}>
                                <td colSpan='2' style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                  Emissions Factors
                                </td>
                              </tr>
                              <tr key={`Scope-${index}`}>
                                <td colSpan='2'>
                                  <span>
                                    <b>Scope {factor?.scope}:</b> {factor?.co2e} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                              <tr key={`Scope3WTT-${index}`}>
                                <td colSpan='2' style={{ display: factor?.wtt ? 'table-cell' : 'none' }}>
                                  <span>
                                    <b>Scope 3 WTT:</b> {factor?.wtt} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
          </div>
        </div>
        </Fragment>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  const metrics = state?.metric?.list;
  const profile = state?.profile.details;
  const { item, adminPortal } = state?.usageDataModal || {};
  const usageTypeDetails = state?.admin?.adminPortal?.usageTypeDetails;
  const { entities, subentities, usageOptions, conversionFactors, filteredCF_UsageOpt } = state || {};
  const facilities = adminPortal
    ? _.filter(state?.admin?.adminPortal?.facilities, { organisationId: item.organisation })
    : state?.facility?.list;

  const currencies = state?.admin?.adminPortal?.currencies;
  const currency = currencies?.find((c) => c.id === item?.currency);
  const metric = metrics?.find((m) => m.id === item?.metric);
  const organisationCurrency = currencies?.find((c) => c.id === state?.organisation?.details?.currency);

  const solarSubentities = ['229591', '233875', '233408'];
  const solarConsumed = !item?.id && item?.metric === '4385' && item?.subentity === '229591';
  const waterTreatment = !item?.id && item?.metric === '4389' && item?.entity === '151938' && item?.subentity !== '185362';

  const orderPriority = {
    entity: 1,
    subentity: 2,
    metric: 3,
    ...usageDimensionOrder,
    currency: 8,
  };

  const facilityOptions = facilities?.map(itemToOptions) || [];
  const facilityValue =
    item?.facility &&
    (_.find(facilityOptions, { value: item.facility }) ||
      _.find(facilityOptions, (opt) => opt.label === item.facility || String(opt.value) === String(item.facility)));

  const options = {
    facility: {
      values: _.sortBy(facilityOptions, ['label']),
      value: facilityValue,
    },
    entity: {
      values: _.sortBy(entities?.map(itemToOptions), ['label']),
      value: item?.entity && _.find(entities?.map(itemToOptions), { value: item?.entity }),
    },
    subentity: {
      values: item?.entity
        ? _.sortBy(subentities?.filter((s) => s?.entity?.includes(item?.entity) && !s?.hideInUI)?.map(itemToOptions), [
            'label',
          ])
        : [],
      title: 'Usage Sub Type',
      value: item?.subentity && _.find(subentities?.map(itemToOptions), { value: item?.subentity }),
    },
  };

  options.metric = {
    values: item?.subentity
      ? _.sortBy(metrics?.filter((m) => options?.subentity?.value?.metric?.includes(m.id))?.map(itemToOptions), ['label'])
      : [],
    title: 'Usage In',
    value: item?.metric && _.find(metrics?.map(itemToOptions), { value: item?.metric }),
  };

  if ((organisationCurrency, options))
    if (usageOptions && item?.metric) {
      const dimensionKeys = sortUsageDimensionKeys(Object.keys(usageOptions), orderPriority);
      dimensionKeys.forEach((usageOpt, index) => {
        const { displayName } = usageTypeDetails?.find((ut) => ut.type === usageOpt) || {};
        const label = displayName ? displayName : usageOpt.charAt(0).toUpperCase() + usageOpt.slice(1) + ' type';
        const previousKey = dimensionKeys[index - 1];
        const previousSelected = index === 0 || !isBlankDimension(item?.[previousKey]);
        const previousUnused =
          index > 0 &&
          Array.isArray(filteredCF_UsageOpt) &&
          filteredCF_UsageOpt.length > 0 &&
          filteredCF_UsageOpt.every((opt) => isBlankDimension(opt?.[previousKey]));

        if (!previousSelected && !previousUnused) return;

        const catalog = usageOptions[usageOpt] || [];
        const constrainKeys = dimensionKeys.slice(0, index);
        const sourceRows =
          index === 0 ? catalog : getUsageOptionsByFilteredCF(usageOptions, filteredCF_UsageOpt, item, constrainKeys)?.[usageOpt];

        if (!sourceRows?.length) return;

        options[usageOpt] = {
          values: _.sortBy(sourceRows.map(itemToOptions), ['label']),
          title: label,
          value: item?.[usageOpt] && _.find(catalog.map(itemToOptions), { value: item?.[usageOpt] }),
        };
      });
    }

  options.currency = {
    values: metric?.isSpend //Only show the currency field if metric is Spend
      ? _.sortBy(
          currencies?.filter((x) => x.hideInUI !== true).map((x) => ({ label: `${x.symbol} (${x.isoCode})`, value: x.id })),
          ['label']
        )
      : [],
    title: 'Usage Data Input Currency',
    value:
      item?.currency &&
      _.find(
        currencies?.filter((x) => x.hideInUI !== true).map((x) => ({ label: `${x.symbol} (${x.isoCode})`, value: x.id })),
        { value: item?.currency }
      ),
  };

  // Require every dimension that is actually on screen. A price type that does not apply
  // to the chosen factor provider is omitted, so it does not block room nights or other providers.
  let usageOptionsOK = true;
  if (usageOptions && Object.keys(usageOptions).length > 0) {
    const shownDimensionKeys = Object.keys(usageOptions).filter((ut) => options[ut]?.values?.length);
    usageOptionsOK = shownDimensionKeys.length > 0 && shownDimensionKeys.every((ut) => !isBlankDimension(item?.[ut]));
  }

  const usageDetails = state?.usageDataModal?.usageDetails;

  const combinedFactors = [...(filteredCF_UsageOpt || []), ...(conversionFactors || [])];

  const fetchedFactors = Array.from(new Set(combinedFactors.map((factor) => factor.id))).map((id) =>
    combinedFactors.find((factor) => factor.id === id)
  );

  const availableFactors = usageDetails?.location?.reportingYear?.conversionFactors
    ? usageDetails?.location?.reportingYear?.conversionFactors
    : usageDetails?.location?.calendarYear?.conversionFactors;

  const compareProperties = (item, usageDetails, usageOptions) => {
    // Compare entity, subentity, metric, dateStart and dateEnd
    if (
      item?.entity !== usageDetails?.entity ||
      item?.subentity !== usageDetails?.subentity ||
      item?.metric !== usageDetails?.metric ||
      item?.dateStart !== usageDetails?.dateStart ||
      item?.dateEnd !== usageDetails?.dateEnd
    ) {
      return false;
    }

    // Compare additional usage options if any
    if (usageOptions) {
      for (const usageType of Object.keys(usageOptions)) {
        if (item?.[usageType] !== usageDetails?.[usageType]) {
          return false;
        }
      }
    }
    return true;
  };

  let factors = availableFactors && compareProperties(item, usageDetails, usageOptions) ? availableFactors : fetchedFactors;

  const orgDetails = state?.organisation?.details;
  const factorYear = factors && getFactorYear(item, orgDetails);

  // Filter factors by selected factor provider and most recent year
  factors = factors?.filter((f) => {
    const factorProviderMatch =
      f?.factorprovider && item?.factorprovider ? f?.factorprovider === item?.factorprovider : true;
    const usageOptionsMatch =
      usageOptions &&
      Object.keys(usageOptions)?.every((usageType) => {
        return isBlankDimension(item?.[usageType]) || String(f[usageType]) === String(item[usageType]);
      });
    const metricMatch = f.metric && item?.metric ? f.metric === item?.metric : true;

    return factorProviderMatch && usageOptionsMatch && metricMatch;
  });

  const closestYear =
    factors &&
    factors
      .map((f) => f.year)
      .filter((year) => year <= factorYear)
      .reduce((prev, curr) => (Math.abs(curr - factorYear) < Math.abs(prev - factorYear) ? curr : prev), -Infinity);

  factors = factors?.filter((f) => Number(f.year) === Number(closestYear));

  const seeDetails = new Map();
  seeDetails.set('Factor Year', (index) => factors?.[index]?.year);
  seeDetails.set('Source', (index) => factors?.[index]?.source);
  //Adding the usage options to the seeDetails map
  Object.keys(usageOptions || {}).forEach((key) => {
    seeDetails.set(_.find(usageTypeDetails, { type: key })?.displayName, (index) => {
      const option = _.find(usageOptions[key], { id: factors?.[index]?.[key] });
      const name = option?.name;
      if (!option?.hideInReporting) {
        return name;
      }
      return null; // Return null if the option is hidden in the Reporting
    });
  });
  seeDetails.set('Usage Sub Type', (index) => _.find(subentities, { id: factors?.[index]?.subentity })?.name);
  seeDetails.set('Metric', (index) => _.find(metrics, { id: factors?.[index]?.metric })?.name);

  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const feature = features?.find((feature) => feature.featureKey === 'FACTOR_DETAILS');
  const hasAccess = availableFeatures?.includes('FACTOR_DETAILS') || feature?.enabledByDefault;
  const aprilFools = features?.find((feature) => feature.featureKey === 'APRIL_FOOLS');
  const hasAprilFools = availableFeatures?.includes('APRIL_FOOLS') || aprilFools?.enabledByDefault;

  return {
    item,
    metric,
    options,
    waterTreatment,
    solarConsumed,
    usageOptionsOK,
    usageOptions: state.usageOptions || {},
    metricTypes: state.metric?.types || {},
    currency,
    adminPortal,
    orderPriority,
    usageTypeDetails,
    solarSubentities,
    conversionFactors,
    organisationCurrency,
    // suppliersEmissionsFactor,
    // useSupplierCarbonFactor,
    hasAprilFools: hasAprilFools || false,
    aprilFools,
    user: state?.profile?.details?.userSub,
    showLoading: state.showLoader,
    progressState: state.progress,
    gridSources: state?.gridSources,
    prevObj: state?.usageDataModal?.prevObj,
    prevDates: state?.usageDataModal?.prevDates,
    usageDetails,
    cyfDataAdmin: profile?.cyfAdmin,
    electricitySources: state?.admin?.adminPortal?.electricitySources,
    metrics,
    seeDetails,
    branding: state?.branding?.colors,
    font: state?.branding?.fonts[0]?.font?.body,
    factors,
    factorDetails: hasAccess || false,
    feature,
    subentities,
    orgCountryAlpha2: (() => {
      const modal = state.usageDataModal;
      const orgRow =
        modal?.adminPortal && modal?.item?.organisation
          ? _.find(state.admin?.organisations, { id: modal.item.organisation })
          : state.organisation?.details;
      const countryId = orgRow?.country;
      if (!countryId) return undefined;
      return _.find(state.organisation?.countries, { id: countryId })?.countryAlpha2Code;
    })(),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getUsageById: (id) => dispatch(USAGEACTIONS.getUsageById(id)),
  getAllItemsFromDB: (table) => dispatch(ADMINACTIONS.getAllItemsFromDB(table)),
  showUsageDataModal: (status, item) => dispatch(USAGEACTIONS.showUsageDataModal(status, item)),
  editUsageModal: (selectedInput, event) => dispatch(USAGEACTIONS.editUsageModal(selectedInput, event)),
  saveUsageData: (usageData, adminPortal) => dispatch(USAGEACTIONS.saveUsageData(usageData, adminPortal)),
  updateUsageYears: (dateStart, dateEnd) => dispatch(ORGANISATIONACTIONS.updateUsageYears(dateStart, dateEnd)),
  resetUsageDataModal: (savedItem) => dispatch(USAGEACTIONS.resetUsageDataModal(savedItem)),
  getFilteredFactors: (year, subentity, gm, metric) =>
    dispatch(USAGEACTIONS.getFilteredFactors(year, subentity, gm, metric)),
  getUsageOptions: (entity) => dispatch(USAGEACTIONS.getUsageOptions(entity)),
  suggestFactorFromText: (payload) => dispatch(USAGEACTIONS.suggestFactorFromText(payload)),
  applyFactorSuggestionToModal: (candidate, opts) => dispatch(USAGEACTIONS.applyFactorSuggestionToModal(candidate, opts)),
  submitFactorSuggestFeedback: (payload) => dispatch(USAGEACTIONS.submitFactorSuggestFeedback(payload)),
  clearFactorSuggestCache: (payload) => dispatch(USAGEACTIONS.clearFactorSuggestCache(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddEditUsage);